import {
  PERMISSION_REGISTRY,
  type PermissionDef,
  type PermissionEntry,
  type PermissionRegistry,
  type PermissionScope,
  scopeLabel,
  sectionRegistry,
  setPermissionScope,
  togglePermission,
} from '@clockwise/shared';
import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PermissionSectionList } from './permission-section-list';

// Reusable permissions editor. Caller owns the array; the editor renders
// rows by grouping a registry and emits the new array on every toggle/scope
// change. State-transition logic lives in @clockwise/shared as pure
// functions (togglePermission / setPermissionScope) so it's testable
// without rendering.
interface Props {
  value: PermissionEntry[];
  onChange: (next: PermissionEntry[]) => void;
  /** Optional override; defaults to the system-role registry. */
  registry?: PermissionRegistry;
  disabled?: boolean;
  /**
   * Server-side validation errors, keyed by permission id. Rendered inline
   * under the affected row (mirroring how the rest of the app surfaces field
   * errors). The API emits `permissions.<index>.<field>` paths; the caller is
   * responsible for resolving an index back to its permission id.
   */
  errors?: Record<string, string>;
}

export function PermissionsEditor({
  value,
  onChange,
  registry = PERMISSION_REGISTRY,
  disabled = false,
  errors,
}: Props) {
  const byId = useMemo(() => new Map(value.map((e) => [e.permission, e])), [value]);
  // Platform-tier permissions (e.g. `tenant.*`) aren't grantable from a tenant
  // role — the API rejects them and preserves any existing ones on its own. So
  // hide them from the editor entirely; the caller's value keeps them untouched.
  // Laid out by section (People, Customers, Time, …), then group.
  const sections = useMemo(
    () => sectionRegistry(registry.filter((d) => d.tier !== 'platform')),
    [registry],
  );

  // One permission: its checkbox, its label, and its scope when it has one.
  const row = (def: PermissionDef) => {
    const entry = byId.get(def.id);
    const checked = entry != null;
    const hasScopes = def.scopes.length > 0;
    const scope = entry?.scope ?? (hasScopes ? 'all' : undefined);
    const error = errors?.[def.id];
    return (
      <li key={def.id} className="py-1.5">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id={`perm-${def.id}`}
            checked={checked}
            onChange={() => onChange(togglePermission(value, def.id, registry))}
            aria-invalid={Boolean(error)}
            className="size-4"
          />
          <Label htmlFor={`perm-${def.id}`} className="flex-1 cursor-pointer text-sm font-normal">
            {def.label}
          </Label>
          {hasScopes ? (
            <Select
              value={scope}
              onValueChange={(v) => {
                // Radix Select can fire a spurious empty `v` during a
                // mount race when the role is already cached (the
                // matching SelectItem registers a tick after the root
                // reads `value`). '' is never a valid scope, so ignore
                // it — otherwise it silently rewrites the row's scope to
                // empty and trips the unsaved-changes guard on load.
                if (!v) return;
                onChange(setPermissionScope(value, def.id, v as PermissionScope));
              }}
              disabled={!checked}
            >
              <SelectTrigger className="h-7 w-40 text-xs" aria-invalid={Boolean(error)}>
                <SelectValue placeholder="Scope" />
              </SelectTrigger>
              <SelectContent>
                {def.scopes.map((s) => (
                  <SelectItem key={s} value={s}>
                    {scopeLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            // Reserve the same width as the scope dropdown so rows
            // line up across permissions in a group.
            <span className="w-40" aria-hidden="true" />
          )}
        </div>
        {error ? <p className="mt-1 pl-7 text-xs text-destructive">{error}</p> : null}
      </li>
    );
  };

  return (
    <fieldset className="space-y-3 rounded-md border p-3" disabled={disabled}>
      <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Permissions ({value.length})
      </legend>
      <PermissionSectionList
        sections={sections}
        count={(section) => {
          const ids = section.groups.flatMap(([, defs]) => defs.map((d) => d.id));
          return `${ids.filter((id) => byId.has(id)).length} of ${ids.length}`;
        }}
        // A row with a server error must be seen: its section stays open.
        keepOpen={(section) =>
          section.groups.some(([, defs]) => defs.some((d) => errors?.[d.id] != null))
        }
        renderRow={row}
      />
    </fieldset>
  );
}
